import type { ChildrenProp } from '../common-types';
import { Button } from '../basic';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { intl } from '../../lib';

export type SimpleCarouselProps = {
  scrollBy: number;
  className?: string;
  triggerForReload?: number | string;
} & Required<ChildrenProp>;

/**
 * A simple carousel component that allows horizontal scrolling of items.
 *
 * @param scrollBy the amount to scroll by when clicking the buttons, in pixels.
 * @param className additional class names to apply to the carousel container.
 * @param triggerForReload if this variable changes the offset and position is recalculated
 * @param children can be any element(s) you need, does not inherit any styling from the carousel.
 * @param props any additional props to pass to the carousel container.
 */
export function SimpleCarousel({
  scrollBy = 50,
  className = '',
  triggerForReload,
  children,
  ...props
}: SimpleCarouselProps): React.JSX.Element {
  const innerContainer = useRef<HTMLDivElement | null>(null);
  const outerContainer = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(0);
  const [maxOffset, setMaxOffset] = useState(0);
  const calcOffset = useCallback(() => {
    if (innerContainer.current && outerContainer.current) {
      return (
        innerContainer.current.clientWidth - outerContainer.current.clientWidth
      );
    }
    return 0;
  }, []);
  const scroll = useCallback(
    (direction: 'left' | 'right' | 'start') => {
      setMaxOffset(calcOffset);
      if (!outerContainer.current) {
        return;
      }
      if (direction == 'start') {
        outerContainer.current.scrollLeft = 0;
        setPosition(0);
      }
      if (direction == 'left') {
        const newPosition = outerContainer.current.scrollLeft - scrollBy;
        outerContainer.current.scrollLeft = newPosition;
        setPosition(newPosition);
      }
      if (direction == 'right') {
        const newPosition = outerContainer.current.scrollLeft + scrollBy;
        outerContainer.current.scrollLeft = newPosition;
        setPosition(newPosition);
      }
    },
    [calcOffset, scrollBy]
  );
  const handleResize = useCallback(() => {
    setMaxOffset(calcOffset);
    scroll('start');
  }, [calcOffset, scroll]);

  useEffect(() => {
    setMaxOffset(calcOffset);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [calcOffset, handleResize, maxOffset, triggerForReload]);

  const classNames = ['ehs-mini-carousel'];
  if (className) {
    classNames.push(className);
  }
  return (
    <div className={classNames.join(' ').trim()} {...props}>
      <Button
        variant="tertiary"
        icon="arrow-line-left-s"
        disabled={position <= 0 || maxOffset === 0}
        onClick={() => scroll('left')}
        text={intl('view.button.previous')}
      />
      <div className="slidingItems" ref={outerContainer}>
        <div ref={innerContainer} className="scrollingContainer">
          {children}
        </div>
      </div>
      <Button
        icon="arrow-line-right-s"
        disabled={position >= maxOffset || maxOffset === 0}
        onClick={() => scroll('right')}
        variant="tertiary"
        text={intl('view.button.next')}
      />
    </div>
  );
}
